/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React from 'react';
import { PREMIUM_EDITION } from '@jira/platform__application-editions/src/edition.tsx';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { AssetsIcon } from '@atlaskit/logo';

import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';
import { useAssetsWorkspaceHost } from '@atlassian/assets-workspace-host/use-assets-workspace-host';
import { getProductContext } from '@atlassian/assets-workspace-host/get-product-context';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { L1_MENU_ID } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import { AssetsUpsellButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-assets/src/ui/assets-app-item/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { AppProps } from '../types.tsx';

const lozenge = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.assets.lozenge',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Try',
	description: 'Growth lozenge for Assets app shortcut',
});

const assets = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.assets.assets',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Assets',
	description: 'Label for Assets app shortcut button',
});

const LEGACY_ASSETS_URL = '/jira/assets';

export function Assets({ onClick }: AppProps): React.JSX.Element {
	const { formatMessage } = useIntl();

	// cross-unit Assets URL.
	const cloudId = useCloudId();
	const appPermissions = useApplicationPermissions();
	const workspaceHostState = useAssetsWorkspaceHost({
		cloudId,
		productContext: getProductContext({ appPermissions }),
		skip: !fg('astral_units_workspace_host_resolver'),
	});

	const isResolverSuccess =
		workspaceHostState.status === 'success' && fg('astral_units_workspace_host_resolver');

	const url = isResolverSuccess
		? workspaceHostState.data.toAbsoluteUrl(LEGACY_ASSETS_URL)
		: LEGACY_ASSETS_URL;

	const appEditions = useAppEditions();
	const isNotJsmPremium = appEditions.serviceDesk !== PREMIUM_EDITION;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isSiteAdmin = useIsSiteAdmin();

	// skip local Premium upsell when resolver confirms cross-unit access.
	if (isNotJsmPremium && !isResolverSuccess && fg('astral_units_workspace_host_resolver')) {
		fireTrackAnalytics(createAnalyticsEvent({}), 'cmdbUpsellPageButton viewed', {
			serviceDeskEdition: appEditions.serviceDesk,
			isSiteAdmin,
		});

		return (
			<AssetsUpsellButton
				configurableMenuId={L1_MENU_ID.GLOBAL_APP_ASSETS}
				lozengeText={formatMessage(lozenge)}
			/>
		);
	}

	return (
		<LinkMenuItem
			href={url}
			elemAfter={<LinkExternalIcon label="" spacing="spacious" size="small" />}
			elemBefore={<AssetsIcon shouldUseNewLogoDesign size="xsmall" />}
			target="_blank"
			onClick={onClick}
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			testId="navigation-apps-sidebar-nav4-sidebars-content-global-app-shortcuts.ui.assets.menu-link-item"
		>
			{formatMessage(assets)}
		</LinkMenuItem>
	);
}
